import { useEffect, useMemo, useState, type ImgHTMLAttributes } from 'react'
import { cn } from '@/shared/lib/utils'
import {
  getOptimizedImageSizes,
  getOptimizedImageSrcSet,
  getOptimizedImageUrl,
  normalizeImageSrc,
  type ImageFit,
  type ImageFormat,
  type ImagePreset,
} from '@/shared/lib/image-optimization'

export interface OptimizedImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet' | 'sizes'> {
  src: string | null | undefined
  alt: string
  preset?: ImagePreset
  width?: number
  height?: number
  quality?: number
  fit?: ImageFit
  format?: ImageFormat
  fallbackSrc?: string
  wrapperClassName?: string
  imageClassName?: string
  sizes?: string
  showSkeleton?: boolean
}

export function OptimizedImage({
  src,
  alt,
  preset = 'card',
  width,
  height,
  quality,
  fit,
  format,
  fallbackSrc = '/placeholder-product.svg',
  wrapperClassName,
  imageClassName,
  className,
  sizes,
  loading = 'lazy',
  decoding = 'async',
  showSkeleton = true,
  onLoad,
  onError,
  ...props
}: OptimizedImageProps) {
  const normalizedSrc = normalizeImageSrc(src)
  const [mode, setMode] = useState<'optimized' | 'original' | 'fallback'>('optimized')
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    setMode('optimized')
    setIsLoaded(false)
  }, [normalizedSrc])

  const optimizedOptions = useMemo(() => ({
    preset,
    width,
    height,
    quality,
    fit,
    format,
  }), [fit, format, height, preset, quality, width])

  const optimizedSrc = useMemo(
    () => getOptimizedImageUrl(normalizedSrc, optimizedOptions),
    [normalizedSrc, optimizedOptions],
  )
  const optimizedSrcSet = useMemo(
    () => getOptimizedImageSrcSet(normalizedSrc, optimizedOptions),
    [normalizedSrc, optimizedOptions],
  )

  const effectiveSrc = mode === 'fallback'
    ? fallbackSrc
    : mode === 'original'
      ? normalizedSrc
      : optimizedSrc

  const effectiveSrcSet = mode === 'optimized' ? optimizedSrcSet : undefined
  const effectiveSizes = mode === 'optimized' ? (sizes || getOptimizedImageSizes(preset)) : undefined

  return (
    <span
      className={cn(
        'relative block overflow-hidden bg-gray-100 dark:bg-gray-800/50',
        wrapperClassName,
      )}
    >
      {showSkeleton && !isLoaded && (
        <span className="absolute inset-0 bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 dark:from-dark-elevated dark:via-dark-card dark:to-dark-elevated">
          <span className="absolute inset-0 -translate-x-full animate-[shimmer_1.4s_infinite] bg-gradient-to-r from-transparent via-white/35 to-transparent dark:via-white/5" />
        </span>
      )}
      <img
        {...props}
        src={effectiveSrc}
        srcSet={effectiveSrcSet}
        sizes={effectiveSizes}
        alt={alt}
        loading={loading}
        decoding={decoding}
        className={cn(
          'block h-full w-full transition-opacity duration-200',
          isLoaded ? 'opacity-100' : 'opacity-0',
          className,
          imageClassName,
        )}
        onLoad={(event) => {
          setIsLoaded(true)
          onLoad?.(event)
        }}
        onError={(event) => {
          setIsLoaded(false)

          if (mode === 'optimized' && optimizedSrc !== normalizedSrc) {
            setMode('original')
          } else if (mode !== 'fallback' && normalizedSrc !== fallbackSrc) {
            setMode('fallback')
          }

          onError?.(event)
        }}
      />
    </span>
  )
}
